import { handleCtaNavigation, handleAnchorLink, handleNavigation } from '@lib/utils/navigation';

/**
 * Navigation directive that can be applied to any element to handle href navigation
 * Supports both CTA objects and simple href strings
 */
export function navigate(
  node: HTMLElement, 
  params: { 
    href?: string; 
    cta?: { action?: string; url?: string; href?: string; target?: '_blank' | '_self' | '_parent' | '_top' }; 
    onCtaClick?: (action?: string) => void;
  }
) {
  let currentParams = params;

  async function handleClick(event: Event) {
    // Prevent default link behavior
    event.preventDefault();
    
    // Handle CTA object navigation
    if (currentParams.cta) {
      await handleCtaNavigation(currentParams.cta, currentParams.onCtaClick);
      return;
    }
    
    // Handle simple href navigation
    if (currentParams.href) {
      // Handle anchor links first
      if (handleAnchorLink(currentParams.href)) {
        return;
      }
      
      // Handle other navigation
      await handleNavigation(currentParams.href);
    }
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick(event);
    }
  }

  function setupElement() {
    // Make element focusable and clickable if it has navigation
    if (currentParams.href || currentParams.cta) {
      node.style.cursor = 'pointer';
      node.setAttribute('role', 'button');
      node.setAttribute('tabindex', '0');
      
      // Add event listeners
      node.addEventListener('click', handleClick);
      node.addEventListener('keydown', handleKeydown);
    }
  }

  function cleanup() {
    node.removeEventListener('click', handleClick);
    node.removeEventListener('keydown', handleKeydown);
    node.removeAttribute('role');
    node.removeAttribute('tabindex');
    node.style.cursor = '';
  }

  // Initial setup
  setupElement();

  return {
    update(newParams: typeof params) {
      cleanup();
      currentParams = newParams;
      setupElement();
    },
    destroy() {
      cleanup();
    }
  };
}

/**
 * Simplified navigation directive for elements that should navigate on click
 * Automatically handles href and CTA navigation patterns
 */
export function navigateOnClick(
  node: HTMLElement,
  href: string
) {
  return navigate(node, { href });
}
